import {
  BIDDER_SETTLEMENT_STATUS_MAP,
  DerivedInvoiceDisplayState,
  InvoicePaymentStatusSchema,
  refundContactLabel,
  refundOnItsWayLabel,
} from '@neon-one/auctions-types';
import type {
  BidderInvoicePaymentContext,
  InvoicePaymentStatus,
  StatusDisplay,
} from '@neon-one/auctions-types';
import { formatCents } from '@/shell/lib/formatCents';

export const CHARGE_STATUS = {
  Processing: 'processing',
  Clearing: 'clearing',
  Paid: 'paid',
  Failed: 'failed',
  AwaitingCheckout: 'awaiting-checkout',
  Refunded: 'refunded',
  PartiallyRefunded: 'partially-refunded',
  Void: 'void',
  Unknown: 'unknown',
} as const;
export type ChargeStatus = typeof CHARGE_STATUS[keyof typeof CHARGE_STATUS];

const INVOICE_STATUS = InvoicePaymentStatusSchema.enum;
const BIDDER_PAYMENT_STATE = { Clearing: 'clearing' } as const;
const CHARGE_DISPLAY: Record<Exclude<ChargeStatus, typeof CHARGE_STATUS.Unknown>, StatusDisplay> = {
  [CHARGE_STATUS.Processing]: BIDDER_SETTLEMENT_STATUS_MAP.processing,
  [CHARGE_STATUS.Clearing]: {
    ...BIDDER_SETTLEMENT_STATUS_MAP.clearing,
    label: `${BIDDER_SETTLEMENT_STATUS_MAP.clearing.label} · ${BIDDER_SETTLEMENT_STATUS_MAP.clearing.qualifier.toLowerCase()}`,
  },
  [CHARGE_STATUS.Paid]: BIDDER_SETTLEMENT_STATUS_MAP.paid_online,
  [CHARGE_STATUS.Failed]: BIDDER_SETTLEMENT_STATUS_MAP.failed,
  [CHARGE_STATUS.AwaitingCheckout]: BIDDER_SETTLEMENT_STATUS_MAP.awaiting,
  [CHARGE_STATUS.Refunded]: BIDDER_SETTLEMENT_STATUS_MAP.refunded,
  [CHARGE_STATUS.PartiallyRefunded]: BIDDER_SETTLEMENT_STATUS_MAP.partially_refunded,
  [CHARGE_STATUS.Void]: BIDDER_SETTLEMENT_STATUS_MAP.void,
};
/** How long a failed refund reads as on its way before the bidder is told to contact the organization. */
export const REFUND_RETRY_GRACE_MS = 7 * 24 * 60 * 60 * 1000;

export function chargeStatusFromPaymentStatus(
  status: InvoicePaymentStatus | null | undefined,
  context: BidderInvoicePaymentContext = {},
): ChargeStatus {
  switch (status) {
    case INVOICE_STATUS.paid: return CHARGE_STATUS.Paid;
    case INVOICE_STATUS.refunded: return CHARGE_STATUS.Refunded;
    case INVOICE_STATUS.partially_refunded: return CHARGE_STATUS.PartiallyRefunded;
    case INVOICE_STATUS.failed: return CHARGE_STATUS.Failed;
    case INVOICE_STATUS.pending: return CHARGE_STATUS.AwaitingCheckout;
    case INVOICE_STATUS.void: return CHARGE_STATUS.Void;
    case INVOICE_STATUS.processing:
      return context.paymentState === BIDDER_PAYMENT_STATE.Clearing
        ? CHARGE_STATUS.Clearing : CHARGE_STATUS.Processing;
    default: return CHARGE_STATUS.Unknown;
  }
}

export function chargeStatusDisplay(status: ChargeStatus): StatusDisplay | null {
  return status === CHARGE_STATUS.Unknown ? null : CHARGE_DISPLAY[status];
}

export function chargeHasReceipt(status: ChargeStatus): boolean {
  return status === CHARGE_STATUS.Paid || status === CHARGE_STATUS.Clearing ||
    status === CHARGE_STATUS.Refunded || status === CHARGE_STATUS.PartiallyRefunded;
}

export function bidderRefundDisplay(
  context: BidderInvoicePaymentContext,
  now = Date.now(),
): StatusDisplay | null {
  const pending = context.pendingRefundCents ?? 0;
  const owedBack = context.owedBackCents ?? 0;
  if (owedBack > 0 && context.oldestRefundFailedAt) {
    const age = now - new Date(context.oldestRefundFailedAt).getTime();
    if (age > REFUND_RETRY_GRACE_MS) {
      return {
        ...BIDDER_SETTLEMENT_STATUS_MAP[DerivedInvoiceDisplayState.RefundFailed],
        label: refundContactLabel(context.organizationName ?? 'the organization'),
      };
    }
  }
  if (pending + owedBack === 0) return null;
  return {
    ...BIDDER_SETTLEMENT_STATUS_MAP[DerivedInvoiceDisplayState.RefundSettling],
    label: refundOnItsWayLabel(formatCents(pending + owedBack)),
  };
}
